import { useState } from 'react';
import {
  createAcquisitionFormDonor,
  submitAcquisitionFormDonation,
  updateAcquisitionFormDonation,
  updateAcquisitionFormDonor
} from '../utils/acquisitionFormApi.js';
import { buildDonationPayload, buildDonorPayload } from '../utils/acquisitionFormPayloads.js';
import { trackLead, trackPaymentInfo } from '../utils/tracking.js';
import { getVocabulary } from '../utils/vocabulary.js';

const CAPTCHA_ERROR = 'Não foi possível validar a verificação de segurança. Atualize a página e tente novamente.';
const CAPTCHA_FAILED = Symbol('captchaFailed');

/**
 * Owns the two calls that leave the browser — donor and donation — plus the
 * submitting flag every step reads to disable its CTA.
 *
 * `onAdvance` is the form's own step navigation: both submissions move forward
 * on success, and the caller decides where forward is.
 */
export function useSubmission({ config, formData, setFormData, captchaRequired, getToken, onAdvance }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function resolveCaptchaToken() {
    if (!captchaRequired) return null;

    try {
      return await getToken();
    } catch {
      setFormData(prev => ({ ...prev, apiError: CAPTCHA_ERROR }));
      return CAPTCHA_FAILED;
    }
  }

  async function submitDonor({ advance = true } = {}) {
    setIsSubmitting(true);
    try {
      const captchaToken = await resolveCaptchaToken();
      if (captchaToken === CAPTCHA_FAILED) return false;

      const nextFormData = { ...formData, apiError: null };
      const payload = buildDonorPayload(config, nextFormData);
      const result = formData.submissionId
        ? await updateAcquisitionFormDonor(formData.submissionId, payload, captchaToken)
        : await createAcquisitionFormDonor(config.AcquisitionFormId, payload, captchaToken);
      const submissionId = result?.submissionId ?? formData.submissionId;
      setFormData(prev => ({
        ...prev,
        submissionId: result?.submissionId ?? prev.submissionId,
        apiError: null
      }));
      trackLead(config, nextFormData, submissionId);
      if (advance) {
        onAdvance();
      }
      return true;
    } catch (error) {
      setFormData(prev => ({ ...prev, apiError: error.message || 'Não foi possível salvar os dados.' }));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitDonation() {
    setIsSubmitting(true);
    try {
      const captchaToken = await resolveCaptchaToken();
      if (captchaToken === CAPTCHA_FAILED) return;

      const payload = await buildDonationPayload(config, formData);
      if (formData.chargeResult) {
        await updateAcquisitionFormDonation(formData.submissionId, payload, captchaToken);
      } else {
        await submitAcquisitionFormDonation(formData.submissionId, payload, captchaToken);
      }
      setFormData(prev => ({
        ...prev,
        chargeResult: null,
        apiError: null
      }));
      trackPaymentInfo(config, formData);
      onAdvance();
    } catch (error) {
      const vocab = getVocabulary(config);
      setFormData(prev => ({ ...prev, apiError: error.message || `Não foi possível concluir ${vocab.the} ${vocab.term}.` }));
    } finally {
      setIsSubmitting(false);
    }
  }

  return { isSubmitting, submitDonor, submitDonation };
}
